import { useEffect, useRef } from 'react';
import { prefersReducedMotion } from './motion';

interface AutoVideoProps {
  src: string;
  poster: string;
  className?: string;
}

/**
 * Full-bleed ambient video: muted, looping, and only playing while on screen.
 * Falls back to the poster image when the user prefers reduced motion.
 */
export default function AutoVideo({ src, poster, className = '' }: AutoVideoProps) {
  const ref = useRef<HTMLVideoElement>(null);

  useEffect(() => {
    const video = ref.current;
    if (!video) return;
    const io = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) video.play().catch(() => {});
        else video.pause();
      },
      { threshold: 0.15 }
    );
    io.observe(video);
    return () => io.disconnect();
  }, []);

  if (prefersReducedMotion()) {
    return (
      <div
        className={`bg-cover bg-center ${className}`}
        style={{ backgroundImage: `url("${poster}")` }}
      />
    );
  }

  return (
    <video
      ref={ref}
      src={src}
      poster={poster}
      muted
      loop
      playsInline
      preload="metadata"
      disablePictureInPicture
      className={`object-cover ${className}`}
    />
  );
}
